import type { Person, PersonId } from '@fs/zion-tree-types'
import React, { type ComponentProps, useMemo } from 'react'
import { events, useSubscribe } from '@fs/zion-tree-pubsub'
import { DataProblemIndicator, RecordHintIndicator, ResearchSuggestionIndicator } from '@fs/zion-research-help'
import { useGroupsContext } from '@fs/zion-tree-groups'
import { useFocusFamilyMembersContext } from '../../contexts/FocusFamilyMembersContext'
import { useResearchHelpContext } from '../../contexts/ResearchHelpContext'
import { getDataProblemSuggestions, getResearchSuggestionSuggestions } from '../../../data-service/researchHelpHelpers'
import { mutateFocusFamilyMembers } from '../../../data-service/useFocusFamilyMembers'

type Props = {
  pid: PersonId
  focusPerson: Person
  size?: ComponentProps<typeof RecordHintIndicator>['size']
}

export default function ResearchHelpIndicator({ pid, focusPerson, size }: Props): JSX.Element | null {
  const { focusFamilyMembers } = useFocusFamilyMembersContext()
  const { currentTreeId } = useGroupsContext()
  const { researchHelpData } = useResearchHelpContext()
  const { refetchDuplicates, recordHints } = researchHelpData
  const dataProblems = getDataProblemSuggestions(researchHelpData)
  const researchSuggestions = getResearchSuggestionSuggestions(researchHelpData)

  // Refetch data when an item is dismissed
  useSubscribe({ evt: events.indicator }, ({ item }) => {
    if (item.type?.name === 'POSSIBLE_DUPLICATE_PERSON') {
      // We only need to refetch duplicates
      refetchDuplicates?.()
    } else {
      // This will recollect all of the data problems and research suggestions after refetching family members
      mutateFocusFamilyMembers(pid, currentTreeId || 'PRIVATE')
    }
  })

  const fullPerson = useMemo(
    () => ({
      ...focusPerson,
      suggestions: { dataQualityList: dataProblems, researchSuggestionList: researchSuggestions },
    }),
    [dataProblems, focusPerson, researchSuggestions]
  )

  if (dataProblems && dataProblems.length > 0) {
    return <DataProblemIndicator familyMembers={focusFamilyMembers} person={fullPerson} size={size} />
  }
  // TODO see if we can up the performance. Right now we make a network call to see if there are records hints by getting full records, and then the RecordHintIndicator makes a network call to display them....
  if (recordHints && recordHints.length > 0 && !focusPerson.readOnly) {
    return (
      <RecordHintIndicator
        familyMembers={focusFamilyMembers}
        person={{ ...fullPerson, hasRecordHints: true }}
        size={size}
      />
    )
  }
  if (researchSuggestions && researchSuggestions.length > 0) {
    return <ResearchSuggestionIndicator familyMembers={focusFamilyMembers} person={fullPerson} size={size} />
  }
  return null
}
